"use strict";
/**
 * @module dynamicSchemaClient
 * @description  Dynamic Schema Client provides an easy way to consume  Dynamic Schema REST API end-points. In order to obtain needed routes `dynamicSchemaClient` uses `dynamicSchemaRoute`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var httpApi_1 = require("../../httpApi");
var _1 = require("./");
var DynamicSchemaClient = /** @class */ (function () {
    function DynamicSchemaClient(dynamicSchemaRoute, apiClient) {
        this.dynamicSchemaRoute = dynamicSchemaRoute;
        this.apiClient = apiClient;
    }
    Object.defineProperty(DynamicSchemaClient.prototype, "routeDefinition", {
        /**
         * Provides direct access to `dynamicSchemaRoute`.
         * @method
         * @example dynamicSchemaClient.routeDefinition.get();
         **/
        get: function () {
            return this.dynamicSchemaRoute;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of dynamic resource schemas matching the given criteria.
     * @method
     * @param options Options object.
     * @example dynamicSchemaClient.find({
                    pageNumber : 1,
                    pageSize : 10,
                    orderBy : '<field>',
                    orderDirection : '<asc|desc>',
                    search : '<search-phrase>'
                })
                .then(function (collection) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                    // perform error handling here
                });
     **/
    DynamicSchemaClient.prototype.find = function (options) {
        return this.apiClient.get(this.routeDefinition.find(options));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns the specified dynamic resource schema.
     * @method
     * @param name Name of dynamic resource schema which need to be retrieved.
     * @param options Options object.
     * @example dynamicSchemaClient.get('<schema-name>')
                    .then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                        // perform error handling here
                    });
     **/
    DynamicSchemaClient.prototype.get = function (name, options) {
        return this.apiClient.get(this.dynamicSchemaRoute.get(name, options));
    };
    /**
     * Returns a promise that is resolved once the create action has been performed; this action creates a new dynamic resource schema item.
     * @method
     * @param data A dynamic resource schema object that needs to be inserted into the system.
     * @example dynamicSchemaClient.create({
                    schema : {
                        type : 'object',
                        properties : {
                            id : {
                                title : '<unique-identifier-field>',
                                readonly : true,
                                hidden : true,
                                type : 'string'
                            },
                            description : {
                                type: string
                            }
                        }
                    },
                    name : '<schema-name>',
                    description : '<description>',
                    enforceSchemaValidation : true
                })
                .then(function (data) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                    // perform error handling here
                });
     **/
    DynamicSchemaClient.prototype.create = function (data) {
        return this.apiClient.post(this.routeDefinition.create(), this.routeDefinition.createParams(data));
    };
    /**
     * Returns a promise that is resolved once the update dynamic resource schema action has been performed; this action updates a dynamic resource schema item. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `dynamicSchemaRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(dynamicResourceSchema);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data A dynamic schema object used to update specified dynamic resource schema.
     * @example // dynamicResourceSchema is a resource previously fetched using get action.
                    dynamicResourceSchema.description = '<description>';
                    dynamicSchemaClient.update(dynamicResourceSchema)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    DynamicSchemaClient.prototype.update = function (data) {
        return this.apiClient.put(this.routeDefinition.update(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the remove action has been performed. This action will remove a dynamic resource schema item from the system if successfully completed. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `dynamicSchemaRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(dynamicResourceSchema);
     * let uri = params['model'].links('delete').href;
     * ```
     * @method
     * @param data A dynamic schema object used to delete specified dynamic resource schema.
     * @example // dynamicResourceSchema is a resource previously fetched using get action.
                    dynamicSchemaClient.remove(dynamicResourceSchema)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    DynamicSchemaClient.prototype.remove = function (data) {
        return this.apiClient.delete(this.routeDefinition.delete(data));
    };
    /**
     * Returns a promise that is resolved once the generate schema action has been performed. Success response returns a schema generated based on the json input.
     * @method
     * @param data Unordered collection of key value pairs used to specify dynamic schema definition.
     * @example baasicDynamicSchemaService.generate({
                    id : '<schema-Id>',
                    description : '<description>'
                })
                .then(function (data) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                    // perform error handling here
                });
     **/
    DynamicSchemaClient.prototype.generate = function (data) {
        return this.apiClient.post(this.routeDefinition.generate(), this.routeDefinition.createParams(data));
    };
    var _a, _b;
    DynamicSchemaClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(_1.TYPES.DynamicSchemaRoute)),
        tslib_1.__param(1, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof _1.DynamicSchemaRoute !== "undefined" && _1.DynamicSchemaRoute) === "function" ? _a : Object, typeof (_b = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _b : Object])
    ], DynamicSchemaClient);
    return DynamicSchemaClient;
}());
exports.DynamicSchemaClient = DynamicSchemaClient;
/**
 * @overview
 ***Notes:**
 - Refer to the [Baasic REST API](http://dev.baasic.com/api/reference/home) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route definition.
 */ 
